import { useCallback, useEffect, useMemo, useState } from 'react'

import { ApprovalStrategy } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  IconButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  Tooltip,
  chakra,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaTrashCanRegular } from '@enechain/ecloud-icons'

import { ReflectAreasModal } from '~/components/approval/ReflectAreasModal'
import {
  type StageLocal,
  hasStageWithoutApprovers,
  makeEmptyStage,
} from '~/components/approval/stage'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { DsMultiSelect } from '~/components/common/DsMultiSelect'
import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'

type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  initialStages: StageLocal[]
  candidates: ApproverCandidate[]
  onDone: (stages: StageLocal[]) => void
  /**
   * 「この設定を他エリアにも反映」ハンドラ (AC-APF-023 / issue #7158)。
   * 選択した反映先エリアの配列を受け取り、当該エリアにのみ上書きする。
   * 未指定 (または reflectAreas 未指定) の場合はトリガーボタンを表示しない
   * (= 全エリア一括設定など、反映の概念が無いコンテキストで本モーダルを再利用するため)。
   */
  onReflectToOtherAreas?: (stages: StageLocal[], destAreas: AreaCode[]) => void
  /**
   * 反映先エリア選択モーダル用 (AC-APF-023)。
   * onReflectToOtherAreas とセットで渡す。反映元エリアは選択肢から除外する。
   */
  reflectAreas?: {
    areas: readonly AreaCode[]
    sourceAreaCode: AreaCode
    areaLabels: Readonly<Record<AreaCode, string>>
  }
  /** 確定ボタンの文言。 未指定は「完了」 (`design/admin/approval-flow.html`)。 */
  confirmLabel?: string
  /**
   * 段階を追加するボタンの置き場所。 `body` (既定) は本文末尾の破線ボタン
   * (`design/admin/approval-flow.html`)、 `footer` は footer 左端の tertiary ボタン
   * (`design/admin/case-templates.html`)。
   */
  addStagePlacement?: 'body' | 'footer'
  /** 段階を追加するボタンの文言。 未指定は「承認段階を追加」。 */
  addStageLabel?: string
}

export const StageEditModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  initialStages,
  candidates,
  onDone,
  onReflectToOtherAreas,
  reflectAreas,
  confirmLabel,
  addStagePlacement = 'body',
  addStageLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [stages, setStages] = useState<StageLocal[]>([])
  /* 反映先エリア選択は専用モーダル (ReflectAreasModal) に出す (issue #7158)。
     選択状態はそちらが持つ — 本モーダルは開閉だけを知っていればよい。 */
  const [isReflectOpen, setIsReflectOpen] = useState(false)

  // 反映先候補 = 反映元を除いた全エリア
  const destAreas = useMemo(
    () =>
      reflectAreas !== undefined
        ? reflectAreas.areas.filter((a) => a !== reflectAreas.sourceAreaCode)
        : [],
    [reflectAreas],
  )

  const showReflectButton =
    onReflectToOtherAreas !== undefined && reflectAreas !== undefined

  // モーダルが開いたとき初期値にリセット (反映先選択モーダルも閉じる)
  useEffect(() => {
    if (isOpen) {
      const init = initialStages.length > 0 ? initialStages : [makeEmptyStage()]
      setStages(
        init.map((s) => ({
          ...s,
          approverMemberIds: [...s.approverMemberIds],
        })),
      )
      setIsReflectOpen(false)
    }
  }, [isOpen, initialStages])

  const handleStrategyChange = useCallback(
    (idx: number, strategy: ApprovalStrategy) => {
      setStages((prev) =>
        prev.map((s, i) => (i === idx ? { ...s, strategy } : s)),
      )
    },
    [],
  )

  /**
   * 段階の承認者を丸ごと差し替える。 DS multi select は「選択後の集合」 を返すので、
   * 追加 / 削除を別ハンドラに分けず 1 本にする。
   */
  const handleApproversChange = useCallback(
    (idx: number, memberIds: string[]) => {
      setStages((prev) =>
        prev.map((s, i) =>
          i === idx ? { ...s, approverMemberIds: memberIds } : s,
        ),
      )
    },
    [],
  )

  const handleRemoveStage = useCallback((idx: number) => {
    setStages((prev) => {
      const next = prev.filter((_, i) => i !== idx)
      return next.length === 0 ? [makeEmptyStage()] : next
    })
  }, [])

  /** 承認段階の上限 (= backend domain も 6段以上を拒否) */
  const MAX_STAGES = 5
  // 非活性の条件とツールチップの理由文は、この 1 つの値から導く (AC-APF-020)。
  // [spec差異] AC-APF-020 は「理由を画面上に文で表示」。 UI 辞書 components/action-reason.md
  // (2026-10-14) の区分 2 はツールチップだけ (常時表示の文は廃止)。 8080 も同じ。
  const addStageDisabledReason =
    stages.length >= MAX_STAGES
      ? t('page:adminApprovalFlow.actions.addStageMaxHint')
      : undefined

  const handleAddStage = useCallback(() => {
    setStages((prev) => {
      if (prev.length >= MAX_STAGES) return prev // 上限ガード
      return [...prev, makeEmptyStage()]
    })
  }, [])

  const addStageLabelText =
    addStageLabel ?? t('page:adminApprovalFlow.actions.addStage')

  const hasUnconfigured = hasStageWithoutApprovers(stages)

  const handleDone = useCallback(() => {
    if (!hasUnconfigured) {
      onDone(stages)
    }
  }, [hasUnconfigured, onDone, stages])

  // 「この設定を他エリアにも反映」 → 反映先選択モーダルを開く
  const handleOpenReflect = useCallback(() => {
    setIsReflectOpen(true)
  }, [])

  /* キャンセル・閉じるでは編集中の段階・承認者を捨てない (issue #7158)。
     stages は本モーダルの state なので、反映モーダルを閉じるだけでよい。 */
  const handleCloseReflect = useCallback(() => {
    setIsReflectOpen(false)
  }, [])

  // 選択したエリアにのみ反映する (AC-APF-023)
  const handleReflect = useCallback(
    (dest: AreaCode[]) => {
      if (onReflectToOtherAreas === undefined || dest.length === 0) return
      setIsReflectOpen(false)
      onReflectToOtherAreas(stages, dest)
    },
    [onReflectToOtherAreas, stages],
  )

  return (
    <>
      <Modal
        /* size は design (承認フロー プロトタイプ) の modal footer に合わせて md (40px)。
         design 側は `.btn`(40px) で、`.btn-sm`(32px) はマトリクス内の副次操作だけに使う。 */
        /* footer の並びは 8080 `admin/approval-flow.html:412-416` と同じ
         「キャンセル (tertiary) / この設定を他エリアにも反映 (secondary) / 完了 (primary)」。
         旧実装はトリガーを tertiary で左端 (marginRight="auto") に離していたが、
         反映は「保存の前に一手間かける副操作」なので primary の直前に置く (issue #7158)。 */
        footer={
          /* 間隔は design `.ecl-modal-footer` の gap: 8px。 「段階を追加」 は ButtonTooltip が
           inline-flex の span で包むため、 ボタン自身の auto margin は flex item に効かない。
           flex item になる外側の Box に margin-right: auto を置いて左端へ寄せる
           (case-templates.html:451)。 */
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            gap="8px"
            width="100%"
          >
            {addStagePlacement === 'footer' && (
              <Box marginRight="auto">
                <ButtonTooltip content={addStageDisabledReason}>
                  <TertiaryButton
                    disabled={addStageDisabledReason !== undefined}
                    onClick={handleAddStage}
                    size="md"
                  >
                    {addStageLabelText}
                  </TertiaryButton>
                </ButtonTooltip>
              </Box>
            )}
            <TertiaryButton onClick={onClose} size="md">
              {t('page:adminApprovalFlow.actions.cancel')}
            </TertiaryButton>
            {showReflectButton && (
              <SecondaryButton onClick={handleOpenReflect} size="md">
                {t('page:adminApprovalFlow.actions.reflectToOtherAreas')}
              </SecondaryButton>
            )}
            <ButtonTooltip
              content={
                hasUnconfigured
                  ? t('page:adminApprovalFlow.stageModal.doneDisabledHint')
                  : undefined
              }
            >
              <PrimaryButton
                disabled={hasUnconfigured}
                onClick={handleDone}
                size="md"
              >
                {confirmLabel ?? t('page:adminApprovalFlow.actions.done')}
              </PrimaryButton>
            </ButtonTooltip>
          </Flex>
        }
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        size="xl"
        title={title}
      >
        <Flex flexDirection="column" gap="12px">
          {subtitle !== undefined && subtitle !== '' && (
            <Box marginTop="2px" color="Text.midEmphasis" fontSize="md">
              {subtitle}
            </Box>
          )}
          {stages.map((stage, idx) => (
            <StageCard
              key={idx}
              candidates={candidates}
              idx={idx}
              onApproversChange={handleApproversChange}
              onRemoveStage={handleRemoveStage}
              onStrategyChange={handleStrategyChange}
              showRemove={stages.length > 1}
              stage={stage}
              t={t}
            />
          ))}
          {/* design の「＋ 承認段階を追加」は `.btn .btn-secondary`(40px) = md。
            footer に置く時 (case-templates.html) は本文には出さない。 */}
          {addStagePlacement === 'body' && (
            <ButtonTooltip content={addStageDisabledReason}>
              <SecondaryButton
                width="100%"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={
                  addStageDisabledReason !== undefined
                    ? 'Border.lowEmphasis'
                    : 'Border.midEmphasis'
                }
                disabled={addStageDisabledReason !== undefined}
                onClick={handleAddStage}
                size="md"
              >
                {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                  埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                  揃わず、 翻訳のたびに記号を書き写す必要もあった。
                  design css `.btn:not(...)>svg` = 20px。 */}
                <FaPlusRegular boxSize={iconSize.labeledButton} />
                {addStageLabelText}
              </SecondaryButton>
            </ButtonTooltip>
          )}
        </Flex>
      </Modal>
      {/* 反映先エリア選択は専用モーダル。承認フロー設定モーダルの**兄弟**として置く
        ことで、閉じても stages (編集中の段階・承認者) が保持される (issue #7158)。 */}
      {showReflectButton && reflectAreas !== undefined && (
        <ReflectAreasModal
          areaLabels={reflectAreas.areaLabels}
          destAreas={destAreas}
          isOpen={isReflectOpen}
          onClose={handleCloseReflect}
          onReflect={handleReflect}
          sourceAreaLabel={reflectAreas.areaLabels[reflectAreas.sourceAreaCode]}
        />
      )}
    </>
  )
}

type StageCardProps = {
  idx: number
  stage: StageLocal
  candidates: ApproverCandidate[]
  showRemove: boolean
  onStrategyChange: (idx: number, strategy: ApprovalStrategy) => void
  onApproversChange: (idx: number, memberIds: string[]) => void
  onRemoveStage: (idx: number) => void
  t: ReturnType<typeof useTranslation>['t']
}

const StageCard = ({
  idx,
  stage,
  candidates,
  showRemove,
  onStrategyChange,
  onApproversChange,
  onRemoveStage,
  t,
}: StageCardProps): React.ReactElement => {
  const selectedIds = new Set(stage.approverMemberIds)

  // 登録済承認者のうち候補不在 (= eauth 上で削除・無効化された「欠落」, AC-APF-026d)。
  // 未設定 (0 人) とは別概念で、 id はあるが無効なので保存はブロックしない
  // (Design Note D2: 警告のみ)。 順序付きで保持し、 欠落チップの序数
  // (approverInactiveLabelNumbered の {{position}}) を欠落者内の連番として算出する。
  const missingApproverIdList = stage.approverMemberIds.filter(
    (id) => !candidates.some((c) => c.memberId === id),
  )
  const hasMissingApprover = missingApproverIdList.length > 0

  /**
   * multi select の選択肢。 候補 (active メンバー) に加えて
   * **欠落した承認者 (eauth 上で ACTIVE でない id) も入れる** (AC-APF-026d)。
   * 入れないと選択済みなのに option が無く、チップごと画面から消えて
   * 「無効なメンバーが含まれている」 警告だけが残り、外す手段が無くなる。
   *
   * 欠落ラベルは全員同じ「(無効)」 なので、 react-select が付ける削除ボタンの
   * accessible name が同名で並ばないよう欠落者内の序数を含める
   * (生の member id は露出しない)。
   */
  const approverOptions = [
    ...candidates.map((c) => ({ value: c.memberId, label: c.displayName })),
    ...missingApproverIdList.map((id, i) => ({
      value: id,
      label: t(
        'page:adminApprovalFlow.stageModal.approverInactiveLabelNumbered',
        { position: i + 1 },
      ),
    })),
  ]

  return (
    <Flex
      flexDirection="column"
      gap="8px"
      padding="16px"
      background="Surface.secondary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
    >
      {/* ヘッダー: 第N承認 + 削除ボタン */}
      <Flex alignItems="center" gap="8px">
        <Box
          as="span"
          textStyle="accordionTitle"
          minWidth="80px"
          color="Text.highEmphasis"
        >
          {t('page:adminApprovalFlow.stageModal.stageNum', { num: idx + 1 })}
        </Box>
        {showRemove && (
          <Tooltip content={t('page:adminApprovalFlow.actions.removeStage')}>
            <IconButton
              marginLeft="auto"
              color="Text.midEmphasis"
              aria-label={t('page:adminApprovalFlow.actions.removeStage')}
              onClick={() => onRemoveStage(idx)}
              size="sm"
              title={t('page:adminApprovalFlow.actions.removeStage')}
              variant="ghost"
            >
              <FaTrashCanRegular aria-hidden="true" />
            </IconButton>
          </Tooltip>
        )}
      </Flex>

      {/* 承認戦略ラジオ */}
      <Flex
        alignItems="center"
        flexWrap="wrap"
        gap="16px"
        aria-label={t('page:adminApprovalFlow.stageModal.strategyGroupLabel')}
        role="radiogroup"
      >
        {([ApprovalStrategy.ALL, ApprovalStrategy.ANY] as const).map(
          (strat) => (
            <chakra.label
              key={strat}
              alignItems="center"
              gap="6px"
              display="inline-flex"
              fontSize="md"
              cursor="pointer"
            >
              <chakra.input
                cursor="pointer"
                checked={stage.strategy === strat}
                name={`apr-strategy-${idx}`}
                onChange={() => onStrategyChange(idx, strat)}
                type="radio"
                value={strat}
              />
              {strat === ApprovalStrategy.ALL
                ? t('page:adminApprovalFlow.strategy.all')
                : t('page:adminApprovalFlow.strategy.any')}
            </chakra.label>
          ),
        )}
      </Flex>

      {/* 承認者 multi-select (AC-APF-018)。

          [2026-08-28 デザイナー指摘] 自前 dropdown を DS の multi select
          (`DsMultiSelect` = DS `Select` の `isMulti`) に置き換えた。自前実装は
          候補リストを `position: absolute` でトリガー直下に出しており、
          **モーダル下部の段階では候補が modal の枠で見切れていた**。
          `DsMultiSelect` は menu を body へ portal するので枠に切られない。
          outside-click / Escape / 検索も react-select 側が持つため、
          `useDropdownDismiss` と `search` / `menuOpen` state は不要になった。 */}
      <DsMultiSelect
        aria-label={t(
          'page:adminApprovalFlow.stageModal.approverSearchPlaceholder',
        )}
        invalid={stage.approverMemberIds.length === 0 || hasMissingApprover}
        noOptionsMessage={t(
          'page:adminApprovalFlow.stageModal.noApproverAvailable',
        )}
        // 候補が 0 件 / 全員選択済みの時の文言。 渡さないと react-select 既定の
        // 英語 ("No options") が出る (devin review 指摘 #6774)。
        onChange={(next) => {
          // **既存の並び順を保つ**。 multi select は `options` の順 (候補 → 欠落)
          // で返すため、 そのまま渡すと保存済み配列が並べ替えられる。
          // `equalStages` は承認者 id を位置で比較するので、 順序が変わるだけで
          // dirty 扱いになる (devin review 指摘 #6774。 段階内の順序は
          // ALL / ANY の判定に意味を持たないので、 見た目の挙動は変わらない)。
          const kept = stage.approverMemberIds.filter((id) => next.has(id))
          const added = [...next].filter(
            (id) => !stage.approverMemberIds.includes(id),
          )
          onApproversChange(idx, [...kept, ...added])
        }}
        options={approverOptions}
        placeholder={t('page:adminApprovalFlow.stageModal.approverPlaceholder')}
        selected={selectedIds}
      />
      {/* 承認者未設定 / 欠落 (AC-APF-026d) の警告。 未設定 (id なし) が優先表示、
          id はあるが候補不在 (欠落) は別メッセージで再設定を促す (D2: 警告のみ、保存はブロックしない)。 */}
      {stage.approverMemberIds.length === 0 ? (
        <Box
          color="Text.negativeHigh"
          style={{ fontSize: '12px', fontWeight: 500 }}
        >
          {t('page:adminApprovalFlow.stageModal.approverUnset')}
        </Box>
      ) : (
        hasMissingApprover && (
          <Box
            color="Text.negativeHigh"
            style={{ fontSize: '12px', fontWeight: 500 }}
          >
            {t('page:adminApprovalFlow.stageModal.approverMissing')}
          </Box>
        )
      )}
    </Flex>
  )
}
